/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from "@angular/core";
import { environment } from "src/environments/environment";
import { DebugEventService } from "./debug-event.service";
import type { fhirR4 } from "@smile-cdr/fhirts";
import { firstValueFrom } from "rxjs";
import { HttpService } from "./http.service";
import { FhirdataService, GroupedBundle } from "./fhirdata.service";
import { get } from "lodash";
import { UtilService } from "./util.service";

export const ORDER_RESOURCE_TYPES = [
  'ServiceRequest',
  'DeviceRequest',
  'NutritionOrder',
  'MedicationRequest',
  'CommunicationRequest',
  'VisionPrescription',
] as const;

export type SupportedOrderTypes =
  | fhirR4.ServiceRequest
  | fhirR4.DeviceRequest
  | fhirR4.NutritionOrder
  | fhirR4.MedicationRequest
  | fhirR4.CommunicationRequest
  | fhirR4.VisionPrescription;

export interface OrderDetail {
  id: string;
  resourceType: string;
  requestedDate?: string | Date;
  orderName?: string;
  occurrenceDateTime?: string | Date;
  occurrencePeriod?: fhirR4.Period;
  testCaseExt?: string;
  status?: string;
  extension?: fhirR4.Extension[];
  code?: any;
}

@Injectable({
  providedIn: "root",
})
export class OrderService {
  static readonly coverageInformationExtUrl = `http://hl7.org/fhir/us/davinci-crd/StructureDefinition/ext-coverage-information`;
  static readonly billingOptionsExtUrl = `http://hl7.org/fhir/us/davinci-crd/StructureDefinition/ext-billing-options`;
  static readonly payloadContentExtUrl = `http://hl7.org/fhir/5.0/StructureDefinition/extension-CommunicationRequest.payload.content`;
  static readonly defaultCoverageUrl = `Coverage?patient={{patientId}}&status=active`;
  patientDetail: fhirR4.Patient;
  orderDetail: SupportedOrderTypes;
  coverage: fhirR4.Coverage;
  performer: fhirR4.Practitioner | fhirR4.Organization;
  requestor: fhirR4.Practitioner;
  patientOrderDetailList: OrderDetail[];
  useQAQuestionnaire = false;
  endpointURLs;
  questionnaireResponse = "";

  constructor(
    private readonly debugService: DebugEventService,
    private readonly httpService: HttpService,
    private readonly fhirService: FhirdataService,
  ) {}

  reset() {
    this.patientDetail = undefined;
    this.orderDetail = undefined;
    this.coverage = undefined;
    this.performer = undefined;
    this.requestor = undefined;
    this.patientOrderDetailList = undefined;
    this.useQAQuestionnaire = undefined;
    this.endpointURLs = undefined;
    this.questionnaireResponse = "";
  }

  async getLatestOrder() {
    this.orderDetail = await firstValueFrom(
      this.httpService.executeGETQueryWithParams<SupportedOrderTypes>(
        `${environment.resourceProviderURI}/${this.orderDetail.resourceType}/${this.orderDetail.id}`,
      ),
    );
    return this.orderDetail;
  }

  async updateOrder(order: SupportedOrderTypes) {
    this.orderDetail = await firstValueFrom(
      this.httpService.executePUT<SupportedOrderTypes>(
        `${environment.resourceProviderURI}/${order.resourceType}/${order.id}`,
        order,
      ),
    );
  }

  async getOrderDetails(resourceType: string, orderId: string) {
    this.reset();
    const getOrdersUrl = `${environment.resourceProviderURI}/${resourceType}?_id=${orderId}&_include=*&_include:iterate=PractitionerRole:practitioner`;
    const data = await firstValueFrom(
      this.httpService.executeGETQueryWithParams<fhirR4.Bundle>(getOrdersUrl),
    );

    if (data.resourceType === "Bundle") {
      const bundle = this.fhirService.processEntriesBundle(data);
      this.patientDetail = bundle.Patient?.[0];
      this.orderDetail =
        bundle.ServiceRequest?.[0] ||
        bundle.DeviceRequest?.[0] ||
        bundle.NutritionOrder?.[0] ||
        bundle.MedicationRequest?.[0] ||
        bundle.CommunicationRequest?.[0] ||
        bundle.VisionPrescription?.[0];
      if (!this.orderDetail) throw new Error("Order not found.");
      this.coverage = await this.getCoverage(this.patientDetail);
      this.endpointURLs = await this.getEndpointUrls(this.coverage);
      await this.getOtherDetails(bundle, this.orderDetail);
    } else {
      console.error(data);
      throw new Error("Received non bundle response");
    }
  }

  async getCoverage(patient: fhirR4.Patient): Promise<fhirR4.Coverage> {
    const coverageUrl = (
      environment.coverageUrl || OrderService.defaultCoverageUrl
    ).replace("{{patientId}}", encodeURIComponent(patient.id));
    const coverageBundle = await firstValueFrom(
      this.httpService.executeGETQueryWithParams<fhirR4.Bundle>(
        `${environment.resourceProviderURI}/${coverageUrl}`,
      ),
    );
    if (coverageBundle.entry?.length === 0) {
      throw new Error("Coverage not found.");
    } else if (coverageBundle.entry.length > 1) {
      throw new Error("Patient should only have one active coverage.");
    } else {
      return coverageBundle.entry[0].resource as fhirR4.Coverage;
    }
  }

  async getOtherDetails(bundle: GroupedBundle, order: SupportedOrderTypes) {
    const refKey = {
      NutritionOrder: "orderer",
      VisionPrescription: "prescriber",
    };

    const requesterRef =
      order.resourceType in refKey
        ? order[refKey[order.resourceType]].reference
        : (order as any).requester?.reference;
    this.requestor = this.determinePractitioner(
      requesterRef,
      bundle.Practitioner,
      bundle.PractitionerRole,
    );
    if (!this.requestor) {
      throw new Error("Requestor reference not found: " + requesterRef);
    }

    this.performer = this.getPerformer(bundle, order);

    const coverageExt = order?.extension?.find(
      (ext) => ext.url === OrderService.coverageInformationExtUrl,
    );
    this.useQAQuestionnaire = !!coverageExt?.extension
      ?.find((ext) => ext.url === "questionnaire")
      ?.valueCanonical?.includes("QAform");

    const responseExt = coverageExt?.extension?.find(
      (ext) => ext.url === "response",
    );

    if (responseExt?.valueReference?.reference?.includes("Questionnaire")) {
      this.questionnaireResponse = responseExt.valueReference.reference;
    }
  }

  getPerformer(bundle: GroupedBundle, order: SupportedOrderTypes) {
    const { resourceType } = order;

    if (resourceType === "CommunicationRequest" && order.sender?.reference?.startsWith("Organization/")) {
      return this.findByReference(order.sender.reference, bundle.Organization);
    }

    const performerRef = this.getPerformerReference(order);
    return this.determinePractitioner(performerRef, bundle.Practitioner, bundle.PractitionerRole);
  }

  private getPerformerReference(order: SupportedOrderTypes): string | undefined {
    switch (order.resourceType) {
      case "ServiceRequest":       return order.performer?.[0]?.reference;
      case "CommunicationRequest": return order.sender?.reference;
      case "DeviceRequest":
      case "MedicationRequest":    return order.performer?.reference;
      default:                     return undefined;
    }
  }

  getIdFromReference(reference: string) {
    const typeAndRef = reference?.split("/") || "";
    return typeAndRef?.[1] || reference;
  }

  determinePractitioner(
    reference: string,
    practitioners: fhirR4.Practitioner[] = [],
    practitionerRoles: fhirR4.PractitionerRole[] = [],
  ): fhirR4.Practitioner | undefined {
    if (!reference) return undefined;

    // Try to find Practitioner directly
    const practitioner = this.findByReference(reference, practitioners);
    if (practitioner) return practitioner;

    // Try to find PractitionerRole and resolve its practitioner reference
    const role = this.findByReference(reference, practitionerRoles);
    if (role?.practitioner?.reference) {
      return this.findByReference(role.practitioner.reference, practitioners);
    }

    return null;
  }

  private findByReference<T extends { id?: string }>(
    reference: string,
    resources: T[] = [],
  ): T | null {
    if (!reference || !Array.isArray(resources)) return null;
    const id = this.getIdFromReference(reference);
    return resources.find((item) => item.id === id) || null;
  }

  async getEndpointUrls(coverage: fhirR4.Coverage) {
    const payorReference = coverage?.payor?.[0]?.reference;

    if (payorReference) {
      const organizationId = payorReference.split("/").pop();

      const organizationEndpointBundle = await firstValueFrom(
        this.httpService.executeGETQueryWithParams<fhirR4.Bundle>(
          `${environment.resourceProviderURI}/Endpoint?_has:Organization:endpoint:_id=${organizationId}`,
        ),
      );

      return Object.fromEntries(
        organizationEndpointBundle?.entry
          ?.filter((entry) => entry.resource?.resourceType === "Endpoint")
          .map((entry) => {
            const endpoint = entry.resource as fhirR4.Endpoint;
            return [endpoint.name, endpoint.address];
          }),
      );
    } else {
      console.error(
        "Payor reference is missing. Unable to fetch payor information.",
      );
      return undefined;
    }
  }

  launchQuestionnaire(task?: fhirR4.Task) {
    const launchArray = this.getResourceReferenceForLaunchUrl(task);
    const sofDtrUrl = `${environment.dtrAppUrl}/?iss=${
      environment.resourceProviderURI
    }&launch=${JSON.stringify(launchArray)}`;
    console.log(sofDtrUrl);
    window.open(
      sofDtrUrl,
      "_blank",
      this.useQAQuestionnaire
        ? null
        : "height=768,width=1024,location=0,menubar=0,status=0,titlebar=0,toolbar=0",
    );
    this.debugService.alert({
      category: "dtr",
      request: sofDtrUrl,
      type: sofDtrUrl ? "success" : "error",
    });
  }

  getResourceReferenceForLaunchUrl(task?: fhirR4.Task) {
    const launchArray = [
      { reference: `${this.coverage?.resourceType}/${this.coverage?.id}` },
      { reference: `Patient/${this.patientDetail.id}` },
    ];
    if (task?.id) {
      launchArray.push({
        reference: `${task.resourceType}/${task.id}`,
      });
    } else if (this.questionnaireResponse) {
      launchArray.push({ reference: `${this.questionnaireResponse}` });
    } else {
      launchArray.push({
        reference: `${this.orderDetail.resourceType}/${this.orderDetail.id}`,
      });
    }
    return launchArray;
  }

  async parseOrderDetails(order: SupportedOrderTypes): Promise<OrderDetail> {
    const resourceType = order.resourceType;
    const dateLabel = {
      ServiceRequest: "authoredOn",
      DeviceRequest: "authoredOn",
      MedicationRequest: "authoredOn",
      CommunicationRequest: "authoredOn",
      NutritionOrder: "dateTime",
      VisionPrescription: "dateWritten",
    };
    const code = await this.getCode(order);
    return {
      id: order.id,
      resourceType: resourceType,
      requestedDate: order[dateLabel[resourceType]],
      orderName: code?.text ?? code?.coding?.[0]?.display,
      ...OrderService.getOccurrence(order),
      testCaseExt: OrderService.crdTestCaseExtension(order),
      status: order.status?.toLowerCase(),
      extension: order.extension,
      code,
    };
  }

  getBillingOptionsCodeableConcept(codeableConcept) {
    return codeableConcept?.extension?.find(
      (ext) => ext.url === OrderService.billingOptionsExtUrl,
    )?.valueCodeableConcept;
  }

  async getCode(order: SupportedOrderTypes) {
    if (order.resourceType === "NutritionOrder") {
      return (
        order.enteralFormula?.baseFormulaType ||
        order.oralDiet?.type?.[0] ||
        order.supplement?.[0]?.type ||
        order.foodPreferenceModifier?.[0]
      );
    } else if (order.resourceType === "MedicationRequest") {
      if (order.medicationCodeableConcept) {
        return (
          this.getBillingOptionsCodeableConcept(
            order.medicationCodeableConcept,
          ) || order.medicationCodeableConcept
        );
      } else if (order.medicationReference?.reference) {
        const medication = await firstValueFrom(
          this.httpService.executeGETQueryWithParams<fhirR4.Medication>(
            `${environment.resourceProviderURI}/${order.medicationReference.reference}`,
          ),
        );
        return medication?.code;
      }
    } else if (order.resourceType === "CommunicationRequest") {
      const payloadExtension = order.payload?.[0]?.extension?.find(
        (ext) => ext.url === OrderService.payloadContentExtUrl,
      ).valueCodeableConcept;
      const billingExtension =
        this.getBillingOptionsCodeableConcept(payloadExtension);
      return billingExtension || payloadExtension;
    } else if (order.resourceType === "VisionPrescription") {
      if (order.lensSpecification) {
        return (
          this.getBillingOptionsCodeableConcept(order.lensSpecification?.[0]) ||
          order.lensSpecification?.[0]?.product
        );
      }
    }

    const code =
      order.resourceType === "ServiceRequest" ? "code" : "codeCodeableConcept";
    return order[code];
  }

  static getOccurrence(order: SupportedOrderTypes) {
    if (order.resourceType === "NutritionOrder") {
      const sched =
        order.enteralFormula?.administration?.[0]?.schedule ||
        order.oralDiet?.schedule?.[0] ||
        order.supplement?.[0]?.schedule?.[0];
      return {
        occurrenceDateTime: sched?.event?.[0],
        occurrencePeriod: sched?.repeat?.boundsPeriod,
      };
    } else if (order.resourceType === "MedicationRequest") {
      const sched = get(order, "dosageInstruction[0].timing.event", []);
      return {
        occurrenceDateTime: [...sched].sort(UtilService.sortDateTime)[0],
      };
    } else if (order.resourceType === "VisionPrescription") {
      return {
        occurrenceDateTime: order.dateWritten,
      };
    }
    return {
      occurrenceDateTime: (order as any).occurrenceDateTime,
      occurrencePeriod: (order as any).occurrencePeriod,
    };
  }

  static crdTestCaseExtension(order: SupportedOrderTypes): string {
    const crdTestCaseExtension = order?.extension?.find(
      (ext) =>
        ext?.url ===
        "https://smiledigitalhealth.com/priorauth/crd-test-case-name",
    );

    return crdTestCaseExtension?.valueCoding?.display || "-";
  }
}
